
import React, { useState, useEffect, useMemo } from 'react';
import { GoogleGenAI } from "@google/genai";
import { useAuth } from '../contexts/AuthContext';
import { subscribeToWorkoutHistory } from '../services/database';
import { WorkoutSession, ExerciseType } from '../types';
import { EXERCISE_CONFIG } from '../constants';
import { Card } from '../components/Card';
import { Button } from '../components/Button';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const Calendar: React.FC = () => {
  const { user, profile } = useAuth();
  const [history, setHistory] = useState<WorkoutSession[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [aiFeedback, setAiFeedback] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    if (user) {
      const unsubscribe = subscribeToWorkoutHistory(user.uid, (data) => {
        setHistory(data);
      });
      return () => unsubscribe();
    } else {
      // Guest mode support
      try {
        const local = JSON.parse(localStorage.getItem('workouts') || '[]');
        setHistory(local);
      } catch(e) {}
    }
  }, [user]);

  // --- Calendar Grid Logic ---
  const currentMonth = selectedDate.getMonth();
  const currentYear = selectedDate.getFullYear();

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const calendarDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null); // Empty slots
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentYear, currentMonth, i));
    }
    return days;
  }, [currentMonth, currentYear]);

  // --- Data Processing ---
  const getWorkoutsForDay = (date: Date) => {
    return history.filter(w => {
      const wDate = new Date(w.date);
      return wDate.getDate() === date.getDate() && 
             wDate.getMonth() === date.getMonth() && 
             wDate.getFullYear() === date.getFullYear();
    });
  };

  const selectedDayWorkouts = getWorkoutsForDay(selectedDate);

  // --- AI Analysis ---
  const runAiAnalysis = async () => {
    if (history.length < 5) return;
    setIsAnalyzing(true);
    setAiFeedback(null);

    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      
      // Split history into "Past" and "Recent" to detect trends
      const sorted = [...history].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      const midpoint = Math.floor(sorted.length / 2);
      const past = sorted.slice(0, midpoint);
      const recent = sorted.slice(midpoint);

      const summary = (sessions: WorkoutSession[]) => 
        sessions.map(s => `${s.exercise}: ${s.reps} reps (${s.accuracyScore}% form)`).join('; ');

      const prompt = `
        Analyze the fitness progress for user ${profile?.nickname || 'Agent'}.
        Fitness Level: ${profile?.fitnessLevel || 'Unknown'}.

        OLD SESSIONS:
        ${summary(past)}

        RECENT SESSIONS:
        ${summary(recent)}

        Compare the recent sessions to the old ones. 
        1. Have they increased reps?
        2. Is their accuracy/form improving?
        3. Are they consistent?
        
        Give a strict, 3-sentence military-style debrief on their growth curve.
      `;

      const response = await ai.models.generateContent({
        model: "gemini-3-pro-preview",
        contents: prompt,
      });

      setAiFeedback(response.text || "Analysis unavailable.");
    } catch (error) {
      console.error(error);
      setAiFeedback("Secure connection to AI core failed.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 pb-24">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tighter">Operations Calendar</h1>
          <p className="text-secondary font-mono text-xs uppercase tracking-widest">Consistency Tracker</p>
        </div>
        <div className="text-right">
           <p className="text-white font-bold uppercase">{selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
        </div>
      </div>

      {/* AI REPORT */}
      <div className="mb-8">
         {!aiFeedback ? (
            <Button 
                variant="secondary" 
                fullWidth 
                onClick={runAiAnalysis} 
                disabled={isAnalyzing || history.length < 3}
            >
                {isAnalyzing ? "Processing Data..." : "Run AI Growth Analysis"}
            </Button>
         ) : (
            <Card className="border-l-4 border-l-green-500 bg-surface/90 animate-in fade-in">
                <h3 className="text-green-500 font-bold uppercase tracking-wide mb-2 text-sm">Growth Debrief</h3>
                <p className="text-slate-300 text-sm font-mono leading-relaxed whitespace-pre-wrap">{aiFeedback}</p>
                <button 
                    onClick={() => setAiFeedback(null)}
                    className="text-[10px] text-slate-500 mt-4 underline hover:text-white"
                >
                    Dismiss
                </button>
            </Card>
         )}
      </div>

      {/* CALENDAR GRID */}
      <div className="bg-surface border border-white/10 p-4 rounded-sm mb-8">
        <div className="grid grid-cols-7 gap-2 mb-2">
            {DAYS.map(d => (
                <div key={d} className="text-center text-[10px] font-mono text-slate-500 uppercase">{d}</div>
            ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
            {calendarDays.map((date, i) => {
                if (!date) return <div key={i} className="aspect-square"></div>;
                
                const workouts = getWorkoutsForDay(date);
                const isToday = new Date().toDateString() === date.toDateString();
                const isSelected = selectedDate.toDateString() === date.toDateString();
                const hasWorkout = workouts.length > 0;

                return (
                    <button 
                        key={i} 
                        onClick={() => setSelectedDate(date)}
                        className={`aspect-square relative border transition-all flex flex-col items-center justify-center
                            ${isSelected ? 'border-white bg-white/10' : 'border-transparent hover:bg-white/5'}
                            ${isToday ? 'text-amber-500 font-bold' : 'text-slate-400'}
                        `}
                    >
                        <span className="text-xs">{date.getDate()}</span>
                        {hasWorkout && (
                            <div className="mt-1 flex gap-0.5">
                                {workouts.slice(0, 3).map((_, idx) => (
                                    <div key={idx} className="w-1 h-1 rounded-full bg-green-500"></div>
                                ))}
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
      </div>

      {/* DAY DETAILS */}
      <div>
          <h2 className="text-lg font-bold text-white uppercase tracking-wide mb-4 border-b border-white/10 pb-2">
            Log: {selectedDate.toLocaleDateString()}
          </h2>
          
          {selectedDayWorkouts.length === 0 ? (
            <p className="text-slate-500 font-mono text-sm uppercase italic">No activity recorded.</p>
          ) : (
            <div className="space-y-3">
                {selectedDayWorkouts.map((session) => (
                    <div key={session.id} className="bg-black/50 border border-white/10 p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white">
                                {session.exercise === ExerciseType.SQUAT && "🦵"}
                                {session.exercise === ExerciseType.PUSHUP && "💪"}
                                {session.exercise === ExerciseType.JUMPING_JACK && "🏃"}
                                {session.exercise === ExerciseType.SITUP && "🍫"}
                            </div>
                            <div>
                                <h4 className="font-bold text-white uppercase text-sm">
                                    {EXERCISE_CONFIG[session.exercise].name}
                                </h4>
                                <div className="flex gap-3 text-[10px] text-slate-400 font-mono mt-1">
                                    <span>{session.reps} REPS</span>
                                    <span>|</span>
                                    <span className={session.accuracyScore > 80 ? 'text-green-500' : 'text-amber-500'}>
                                        {session.accuracyScore}% ACCURACY
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
          )}
      </div>
    </div>
  );
};